import { Badge, BottomNavigationActionProps, Box } from '@mui/material'
import { MouseEventHandler } from 'react'
import { Icons } from 'utoniq-ui'

import { useBrowserLink } from '@/components/lib/BrowserLink'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { usePrivateAction } from '@/lib/privateAction'
import { GlobalTabKeys } from '@/types/tabs'

export type TabList = {
  [key in GlobalTabKeys[number]]: {
    label: BottomNavigationActionProps['label']
    icon: BottomNavigationActionProps['icon']
    value: BottomNavigationActionProps['value']
    href: string
    onClick?: MouseEventHandler<HTMLAnchorElement>
  }
}
type UseTabListProps = {
  /** 配送申請・コンビニ支払いなどが必要な状態であればtrue */
  hasManagementTask?: boolean
  /** True if account settings is required */
  hasSettingTask?: boolean
  /** 未読の通知数 */
  unreadNotificationCount?: number
}

export const useTabList = ({
  hasManagementTask,
  hasSettingTask,
  unreadNotificationCount = 0,
}: UseTabListProps): TabList => {
  const { t } = useTranslation()
  const { handleLoginModal } = usePrivateAction()
  const { routerPages, generatePath } = useRouter()
  const { onClick: onCalenderClick } = useBrowserLink(
    config.calendarUrl ?? '',
    {
      presentationStyle: 'popover',
    }
  )

  return {
    home: {
      label: t('home'),
      icon: <Icons.Home color="inherit" />,
      value: 'home',
      href: routerPages.GlobalHome,
    },
    'campaign-events': {
      label: t('event'),
      icon: <Icons.Event color="inherit" />,
      value: 'campaign-events',
      href: generatePath(routerPages.GlobalEvents),
      onClick: handleLoginModal(),
    },
    calendar: {
      label: t('calendar'),
      icon: <Icons.Calendar color="inherit" />,
      value: 'calendar',
      href: '',
      onClick: onCalenderClick,
    },
    notifications: {
      label: t('notification'),
      icon: (
        <Box sx={{ display: 'inline-flex', position: 'relative' }}>
          <Badge
            badgeContent={unreadNotificationCount ? '' : undefined}
            color={'like' as any}
            sx={{
              position: 'absolute',
              color: 'white',
              top: 4,
              right: -2,
              width: 12,
              height: 12,
              '& > span': {
                fontWeight: 'bold',
                fontSize: 9,
                minWidth: 12,
                height: 12,
                bgcolor: 'like.tap',
              },
            }}
          />
          <Icons.Notice color="inherit" />
        </Box>
      ),
      value: 'notifications',
      href: routerPages.Notifications,
      onClick: handleLoginModal(),
    },
    myProfile: {
      label: t('myProfile'),
      icon: (
        <Box sx={{ display: 'inline-flex', position: 'relative' }}>
          <Badge
            badgeContent={hasManagementTask || hasSettingTask ? '' : undefined}
            color={'like' as any}
            sx={{
              position: 'absolute',
              color: 'white',
              top: 4,
              right: -2,
              width: 12,
              height: 12,
              '& > span': {
                fontWeight: 'bold',
                fontSize: 9,
                minWidth: 12,
                height: 12,
                bgcolor: 'like.tap',
              },
            }}
          />
          <Icons.MyPage color="inherit" />
        </Box>
      ),
      value: 'myProfile',
      href: routerPages.MyProfile,
      onClick: handleLoginModal(),
    },
    myCollections: {
      label: t('myCollections'),
      icon: <Icons.Collection color="inherit" />,
      value: 'myCollections',
      href: routerPages.GlobalCollections,
      onClick: handleLoginModal(),
    },
    artist: {
      label: t('artistSearch'),
      icon: <Icons.Search color="inherit" />,
      value: 'artist',
      href: routerPages.GlobalArtist,
    },
    events: {
      label: t('event'),
      icon: <Icons.ShokufesEvent color="inherit" />,
      value: 'events',
      href: routerPages.ShokufesEvents,
    },
    pickup: {
      label: t('pickup'),
      icon: <Icons.PickUp color="inherit" />,
      value: 'pickup',
      href: routerPages.MyOwnedTicketsEventSelection,
      onClick: handleLoginModal(),
    },
  } as const
}
